import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Image as KonvaImage, Text, Transformer, Rect } from 'react-konva';
import useImage from '../../hooks/useImage';
import { productAPI, techniqueAPI, delayAPI, estimateAPI, orderAPI } from '../../services/api';
import { Button, FormField, Input, Textarea, Select, Modal } from '../../components/common';
import { toast } from 'react-toastify';
import './StudioPage.css';

/* ── Konva Image wrapper ─────────────────────────── */
const URLImage = ({ url, ...props }) => {
  const [img] = useImage(url, 'anonymous');
  return <KonvaImage image={img} {...props} />;
};

/* ── Draggable / Resizable element avec contrainte zone d'impression ── */
const StudioElement = ({ el, isSelected, onSelect, onChange, printZone }) => {
  const shapeRef = useRef();
  const trRef    = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const [img] = useImage(el.type === 'image' ? el.src : null, 'anonymous');

  // Contraindre la position dans la zone d'impression
  const clampToZone = (node) => {
    const pz   = printZone;
    const sX   = node.scaleX();
    const sY   = node.scaleY();
    const w    = (node.width()  || 0) * Math.abs(sX);
    const h    = (node.height() || 0) * Math.abs(sY);
    const newX = Math.max(pz.x, Math.min(pz.x + pz.w - w, node.x()));
    const newY = Math.max(pz.y, Math.min(pz.y + pz.h - h, node.y()));
    return { x: newX, y: newY };
  };

  // Contraindre le resize dans la zone
  const clampResize = (oldBox, newBox) => {
    if (newBox.width < 20 || newBox.height < 20) return oldBox;
    const pz = printZone;
    if (newBox.x < pz.x || newBox.y < pz.y) return oldBox;
    if (newBox.x + newBox.width  > pz.x + pz.w) return oldBox;
    if (newBox.y + newBox.height > pz.y + pz.h) return oldBox;
    return newBox;
  };

  const commonProps = {
    ref: shapeRef,
    x: el.x, y: el.y,
    rotation: el.rotation || 0,
    scaleX: el.scaleX || 1,
    scaleY: el.scaleY || 1,
    draggable: true,
    onClick:   (e) => { e.cancelBubble = true; onSelect(el.id); },
    onTap:     (e) => { e.cancelBubble = true; onSelect(el.id); },
    onDragMove: (e) => {
      // Contrainte en temps réel pendant le drag
      const { x, y } = clampToZone(e.target);
      e.target.x(x);
      e.target.y(y);
    },
    onDragEnd: (e) => {
      const { x, y } = clampToZone(e.target);
      e.target.position({ x, y });
      onChange({ ...el, x, y });
    },
    onTransformEnd: (e) => {
      const node = shapeRef.current;
      const { x, y } = clampToZone(node);
      onChange({
        ...el,
        x,
        y,
        scaleX:   node.scaleX(),
        scaleY:   node.scaleY(),
        rotation: node.rotation(),
      });
    },
  };

  const textProps = el.type === 'text' ? {
    text:       el.text || ' ',
    fontSize:   el.fontSize || 24,
    fontFamily: el.fontFamily || 'Arial',
    fill:       el.fill || '#000000',
    fontStyle:  el.bold ? 'bold' : el.italic ? 'italic' : 'normal',
    align:      el.align || 'left',
    width:      el.width || undefined,
    wrap:       'word',
  } : {};

  return (
    <>
      {el.type === 'text' ? (
        <Text {...commonProps} {...textProps} />
      ) : (
        <KonvaImage
          {...commonProps}
          image={img}
          width={el.width || 100}
          height={el.height || 100}
        />
      )}
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={clampResize}
          rotateEnabled={true}
          keepRatio={el.type === 'image'}
          enabledAnchors={['top-left','top-right','bottom-left','bottom-right','middle-left','middle-right']}
        />
      )}
    </>
  );
};

/* ── Main Studio ─────────────────────────────────── */
const StudioPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product,    setProduct]    = useState(null);
  const [techniques, setTechniques] = useState([]);
  const [delays,     setDelays]     = useState([]);
  const [loading,    setLoading]    = useState(true);

  // Studio state
  const [activeFace,    setActiveFace]    = useState('front'); // nom de face, pas index
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedTech,  setSelectedTech]  = useState('');
  const [selectedDelay, setSelectedDelay] = useState('');
  const [elements,      setElements]      = useState({}); // { face_index: [...elements] }
  const [selectedId,    setSelectedId]    = useState(null);

  // Text tool
  const [addTextModal, setAddTextModal]  = useState(false);
  const [textConfig,   setTextConfig]    = useState({ text: '', fontSize: 24, fill: '#000000', fontFamily: 'Arial', bold: false, italic: false });

  // Quantities
  const [quantities, setQuantities] = useState({});
  const totalQty = Object.values(quantities).reduce((s, v) => s + (parseInt(v) || 0), 0);

  // Quote form
  const [quoteModal, setQuoteModal] = useState(false);
  const [quoteForm,  setQuoteForm]  = useState({ contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '', contact_company: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  // Estimate
  const [estimate, setEstimate] = useState(null);

  const stageRef  = useRef();
  const wrapperRef = useRef();
  const [stageScale, setStageScale] = React.useState(1);

  // Current mockup
  const mockups = product?.mockups || [];

  // Faces uniques (sans doublons couleur)
  const uniqueFaces = [...new Set(mockups.map(m => m.face))];
  // S'assurer qu'activeFace est une face valide
  const currentFace = uniqueFaces.includes(activeFace) ? activeFace : (uniqueFaces[0] || 'front');

  // Normalise un nom de couleur : "Bleu-Marine" = "Bleu Marine" = "bleu marine"
  const normColor = (s) => (s || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Trouver la maquette pour la face active ET la couleur sélectionnée
  const getMockupForColor = (faceName) => {
    const sameFace = mockups.filter(m => m.face === faceName);
    if (!sameFace.length) return null;

    if (selectedColor) {
      // 1. Match par color_ref (nom de couleur dans le nom de fichier)
      if (selectedColor.name) {
        const nameNorm = normColor(selectedColor.name);
        const refMatch = sameFace.find(m =>
          m.color_ref && normColor(m.color_ref) === nameNorm
        );
        if (refMatch) return refMatch;
      }
      // 2. Match par color_hex — teste tous les hexes de la variante
      const allHexes = (selectedColor.hexes?.length > 0 ? selectedColor.hexes : [selectedColor.hex]).map(h => h.toLowerCase());
      const hexMatch = sameFace.find(m =>
        m.color_hex && allHexes.includes(m.color_hex.toLowerCase())
      );
      if (hexMatch) return hexMatch;
    }
    // Fallback : générique (sans color_ref) → blanc → première de la face
    return sameFace.find(m => !m.color_ref && !m.color_hex)
        || sameFace.find(m => m.color_ref && /blanc|white/i.test(m.color_ref))
        || sameFace[0];
  };

  const currentMockup = getMockupForColor(currentFace);
  const printZone = currentMockup?.print_zone || { x: 150, y: 100, w: 200, h: 200 };
  const canvasW   = currentMockup?.canvas_w || 600;
  const canvasH   = currentMockup?.canvas_h || 600;

  // Calculer le scale CSS pour adapter le Stage à la zone disponible
  React.useEffect(() => {
    const updateScale = () => {
      if (!wrapperRef.current) return;
      const wrapper = wrapperRef.current;
      const availW = wrapper.clientWidth  - 48;
      const availH = wrapper.clientHeight - 48;
      const scaleW = availW > 0 ? availW / canvasW : 1;
      const scaleH = availH > 0 ? availH / canvasH : 1;
      setStageScale(Math.min(scaleW, scaleH, 1));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [canvasW, canvasH]);

  // Current face elements (clé = nom de face)
  const faceElements = elements[currentFace] || [];
  const setFaceElements = (els) => setElements(prev => ({ ...prev, [currentFace]: els }));

  useEffect(() => {
    if (!productId) { navigate('/catalogue'); return; }
    Promise.all([
      productAPI.get(productId),
      techniqueAPI.list(),
      delayAPI.list(),
    ]).then(([pRes, tRes, dRes]) => {
      const p = pRes.data.data;
      setProduct(p);
      if (p.available_colors?.length) {
        const white = p.available_colors.find(c => /blanc|white/i.test(c.name));
        setSelectedColor(white || p.available_colors[0]);
      }
      if (p.available_sizes?.length) {
        const qInit = {};
        p.available_sizes.forEach(s => { qInit[s] = 0; });
        setQuantities(qInit);
      }
      setTechniques(tRes.data.data || []);
      setDelays(dRes.data.data || []);
    }).catch(() => navigate('/catalogue'))
      .finally(() => setLoading(false));
  }, [productId, navigate]);

  // Calcul estimatif
  useEffect(() => {
    if (!productId || !totalQty || !selectedTech) { setEstimate(null); return; }
    const timer = setTimeout(() => {
      estimateAPI.calculate({
        product_id: parseInt(productId),
        technique_id: parseInt(selectedTech),
        production_delay_id: selectedDelay ? parseInt(selectedDelay) : null,
        quantities,
      }).then(({ data }) => setEstimate(data.data)).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [productId, totalQty, selectedTech, selectedDelay, quantities]);

  const addText = () => {
    if (!textConfig.text.trim()) return;
    const newEl = {
      id: `text_${Date.now()}`,
      type: 'text',
      x: printZone.x + 20,
      y: printZone.y + 20,
      ...textConfig,
      scaleX: 1, scaleY: 1, rotation: 0,
    };
    setFaceElements([...faceElements, newEl]);
    setSelectedId(newEl.id);
    setAddTextModal(false);
    setTextConfig({ text: '', fontSize: 24, fill: '#000000', fontFamily: 'Arial', bold: false, italic: false });
  };

  const addImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const ratio = Math.min(150 / img.width, 150 / img.height);
        const newEl = {
          id: `img_${Date.now()}`,
          type: 'image',
          src: ev.target.result,
          x: printZone.x + 10,
          y: printZone.y + 10,
          width:  img.width  * ratio,
          height: img.height * ratio,
          scaleX: 1, scaleY: 1, rotation: 0,
        };
        setFaceElements([...faceElements, newEl]);
        setSelectedId(newEl.id);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setFaceElements(faceElements.filter(el => el.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, faceElements]);

  const resetFace = () => {
    setElements(prev => ({ ...prev, [currentFace]: [] }));
    setSelectedId(null);
  };

  const resetAll = () => {
    setElements({});
    setSelectedId(null);
    setSelectedTech('');
    setSelectedDelay('');
    const qReset = {};
    Object.keys(quantities).forEach(k => { qReset[k] = 0; });
    setQuantities(qReset);
    toast.info('Studio réinitialisé');
  };


  const downloadDesign = async () => {
    toast.info('Génération en cours…');
    try {
      const mockup = currentMockup;
      const cw     = mockup?.canvas_w   || 600;
      const ch     = mockup?.canvas_h   || 600;
      const PR     = 2;

      // Canvas 2D offscreen — jamais de crossOrigin → jamais tainted
      const canvas = document.createElement('canvas');
      canvas.width  = cw * PR;
      canvas.height = ch * PR;
      const ctx = canvas.getContext('2d');
      ctx.scale(PR, PR);

      // Fond blanc
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cw, ch);

      // Charger la maquette via XMLHttpRequest → blob:// (contourne le tainted canvas)
      if (mockup?.image_url) {
        await new Promise(res => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', mockup.image_url, true);
          xhr.responseType = 'blob';
          xhr.onload = () => {
            if (xhr.status === 200) {
              const objUrl = URL.createObjectURL(xhr.response);
              const img = new Image();
              img.onload  = () => { ctx.drawImage(img, 0, 0, cw, ch); URL.revokeObjectURL(objUrl); res(); };
              img.onerror = () => { URL.revokeObjectURL(objUrl); res(); };
              img.src = objUrl;
            } else { res(); }
          };
          xhr.onerror = () => res();
          xhr.send();
        });
      }

      // Éléments du client (textes + images data URI)
      for (const el of faceElements) {
        ctx.save();
        const cx = (el.x || 0) + (el.width  || 0) / 2;
        const cy = (el.y || 0) + (el.height || 0) / 2;
        ctx.translate(cx, cy);
        ctx.rotate(((el.rotation || 0) * Math.PI) / 180);

        if (el.type === 'text') {
          const bold   = el.fontStyle?.includes('bold')   ? 'bold '   : '';
          const italic = el.fontStyle?.includes('italic') ? 'italic ' : '';
          ctx.font         = `${italic}${bold}${el.fontSize || 24}px ${el.fontFamily || 'Arial'}`;
          ctx.fillStyle    = el.fill || '#000000';
          ctx.textAlign    = el.align === 'right' ? 'right' : el.align === 'left' ? 'left' : 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(el.text || '', 0, 0);

        } else if (el.type === 'image' && el.src) {
          // Les images client sont des data URI → jamais de taint
          await new Promise(res => {
            const img = new Image();
            img.onload  = () => {
              ctx.drawImage(img, -(el.width||100)/2, -(el.height||100)/2, el.width||100, el.height||100);
              res();
            };
            img.onerror = () => res();
            img.src = el.src; // data URI — pas de CORS
          });
        }
        ctx.restore();
      }

      // Export propre via toBlob (canvas local → jamais insecure)
      canvas.toBlob(blob => {
        if (!blob) { toast.error('Erreur génération image'); return; }
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `design_${product?.reference || 'produit'}_${currentFace}.png`;
        link.href     = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 3000);
        toast.success('Design téléchargé !');
      }, 'image/png');

    } catch (err) {
      console.error('Download error:', err);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const updateElement = (updated) => {
    setFaceElements(faceElements.map(el => el.id === updated.id ? updated : el));
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    if (!quoteForm.contact_email) {
      toast.error('Email requis');
      return;
    }
    if (!quoteForm.contact_first_name && !quoteForm.contact_last_name) {
      toast.error('Prénom ou nom requis');
      return;
    }
    if (!totalQty) { toast.error('Veuillez indiquer des quantités'); return; }
    if (!selectedTech) { toast.error('Veuillez choisir une technique'); return; }

    setSubmitting(true);
    try {
      const faceLabels = { front: 'Avant', back: 'Arrière', left: 'Gauche', right: 'Droite' };

            // Résumé lisible des éléments par face
      const faceSummaries = {};
      Object.entries(elements).forEach(([face, els]) => {
        if (!Array.isArray(els) || els.length === 0) return;
        faceSummaries[face] = {
          label:    faceLabels[face] || face,
          count:    els.length,
          elements: els.map(el => ({
            type:       el.type,
            text:       el.type === 'text' ? el.text : null,
            fontSize:   el.type === 'text' ? el.fontSize : null,
            fill:       el.type === 'text' ? el.fill : null,
            fontFamily: el.type === 'text' ? el.fontFamily : null,
            hasImage:   el.type === 'image',
            x:          Math.round(el.x || 0),
            y:          Math.round(el.y || 0),
          })),
        };
      });

      // Infos maquettes par face (pour reconstruire le rendu côté backoffice)
      const mockupInfo = {};
      uniqueFaces.forEach(face => {
        const m = getMockupForColor(face);
        if (m) mockupInfo[face] = {
          image_url:   m.image_url,
          canvas_w:    m.canvas_w,
          canvas_h:    m.canvas_h,
          print_zone:  m.print_zone,
          face:        m.face,
        };
      });

      const designData = {
        product_id:     parseInt(productId),
        faces:          elements,
        face_summaries: faceSummaries,
        mockup_info:    mockupInfo,
        color:          selectedColor,
        technique_id:   parseInt(selectedTech),
        total_quantity: totalQty,
      };

      // Fusionner first_name + last_name en contact_name (attendu par le backend)
      const contactName = [quoteForm.contact_first_name, quoteForm.contact_last_name]
        .filter(Boolean).join(' ');

      const { data } = await orderAPI.submit({
        ...quoteForm,
        contact_name:         contactName,
        product_id:           parseInt(productId),
        technique_id:         parseInt(selectedTech),
        production_delay_id:  selectedDelay ? parseInt(selectedDelay) : null,
        quantities,
        design_data:          designData,
        estimated_price:      estimate?.total_ht || 0,
      });

      toast.success(`Demande envoyée ! Référence : ${data.data?.reference}`);
      setQuoteModal(false);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  // Précharger la maquette via fetch→blob (doit être avant tout early return)
  const mockupImageUrl = currentMockup?.image_url || null;
  // mockupBlobUrl supprimé — CORS configuré côté serveur


  if (loading) return (
    <div className="studio-loading">
      <div className="spinner spinner-lg" />
      <p>Chargement du studio…</p>
    </div>
  );

  if (!product) return null;

  const selectedEl = faceElements.find(el => el.id === selectedId);

  return (
    <div className="studio-page">
      {/* ── Sidebar gauche ── */}
      <aside className="studio-sidebar">
        <div className="studio-sidebar-inner">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Produit</h3>
            <div className="product-mini">
              <strong>{product.name}</strong>
              <span className="text-muted" style={{fontSize:'12px'}}>Réf. {product.reference}</span>
            </div>
          </div>

          {/* Faces */}
          {mockups.length > 1 && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Faces</h3>
              <div className="face-tabs">
                {uniqueFaces.map(face => {
                  const faceLabel = { front:'Avant', back:'Arrière', left:'Gauche', right:'Droite', custom:'Autre' }[face] || face;
                  const count = (elements[face] || []).length;
                  return (
                    <button
                      key={face}
                      className={`face-tab${currentFace === face ? ' active' : ''}`}
                      onClick={() => { setActiveFace(face); setSelectedId(null); }}
                    >
                      {faceLabel}
                      {count > 0 && <span className="face-badge">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Couleur du vêtement */}
          {product.available_colors?.length > 0 && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Couleur du vêtement</h3>
              <div className="color-picker">
                {product.available_colors.map((c, ci) => {
                  const hexes = c.hexes?.length > 0 ? c.hexes : [c.hex || '#000000'];
                  const isSelected = selectedColor?.name === c.name;
                  const swatchStyle = hexes.length === 1
                    ? { background: hexes[0] }
                    : { background: `conic-gradient(${hexes.map((h, i) => `${h} ${i * (360/hexes.length)}deg ${(i+1) * (360/hexes.length)}deg`).join(',')})` };
                  return (
                    <button
                      key={c.name + ci}
                      className={`color-swatch${isSelected ? ' selected' : ''}`}
                      style={swatchStyle}
                      title={c.name}
                      onClick={() => setSelectedColor({ ...c, hexes })}
                    />
                  );
                })}
              </div>
              {selectedColor && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  {(() => {
                    const hexes = selectedColor.hexes?.length > 0 ? selectedColor.hexes : [selectedColor.hex || '#000000'];
                    const style = hexes.length === 1
                      ? { background: hexes[0] }
                      : { background: `conic-gradient(${hexes.map((h, i) => `${h} ${i * (360/hexes.length)}deg ${(i+1) * (360/hexes.length)}deg`).join(',')})` };
                    return <div style={{ width: 18, height: 18, borderRadius: '50%', ...style, border: '2px solid var(--border)', flexShrink: 0 }} />;
                  })()}
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedColor.name}</span>
                </div>
              )}
            </div>
          )}

          {/* Technique */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Technique de marquage</h3>
            <div className="technique-list">
              {techniques.map((t) => (
                <button
                  key={t.id}
                  className={`technique-btn${String(selectedTech) === String(t.id) ? ' active' : ''}`}
                  onClick={() => setSelectedTech(String(t.id))}
                >
                  <span className="technique-btn-name">{t.name}</span>
                  <span className="technique-btn-price">+{t.price_per_unit}€/pce</span>
                </button>
              ))}
            </div>
          </div>

          {/* Délai */}
          <div className="sidebar-section">
            <h3 className="sidebar-title">Délai de production</h3>
            <select
              className="form-input form-select"
              value={selectedDelay}
              onChange={(e) => setSelectedDelay(e.target.value)}
            >
              <option value="">Choisir un délai…</option>
              {delays.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}{d.surcharge > 0 ? ` (+${d.surcharge}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Liste des éléments sur le canvas */}
          {faceElements.length > 0 && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Éléments sur le canvas</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {faceElements.map((el, i) => (
                  <div
                    key={el.id}
                    onClick={() => setSelectedId(el.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px', borderRadius: 'var(--radius-sm)',
                      border: `1.5px solid ${selectedId === el.id ? 'var(--brand-accent)' : 'var(--border)'}`,
                      background: selectedId === el.id ? 'rgba(230,57,70,.06)' : 'var(--bg-page)',
                      cursor: 'pointer', transition: 'all .15s', fontSize: 13,
                    }}
                  >
                    <span>{el.type === 'text' ? '✏️' : '🖼'}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: selectedId === el.id ? 700 : 400 }}>
                      {el.type === 'text' ? (el.text || '(texte vide)') : `Image ${i + 1}`}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFaceElements(faceElements.filter(x => x.id !== el.id)); if (selectedId === el.id) setSelectedId(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, padding: '0 2px', lineHeight: 1 }}
                      title="Supprimer"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="sidebar-section sidebar-actions">
            <button className="action-btn" onClick={resetFace} title="Réinitialiser la face">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
              Réinitialiser face
            </button>
            <button className="action-btn" onClick={resetAll} title="Tout réinitialiser">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              Tout réinitialiser
            </button>
          </div>
        </div>
      </aside>

      {/* ── Zone centrale studio ── */}
      <main className="studio-canvas-area">
        {/* Toolbar */}
        <div className="studio-toolbar">
          <div className="toolbar-left">
            <button className="tool-btn" onClick={() => setAddTextModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
              Ajouter texte
            </button>
            <label className="tool-btn tool-btn-file">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Ajouter image
              <input type="file" accept="image/*" onChange={addImage} hidden />
            </label>
            {selectedId && (
              <button className="tool-btn tool-btn-danger" onClick={deleteSelected}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                Supprimer
              </button>
            )}
          </div>
          <div className="toolbar-right">
            <button className="tool-btn tool-btn-outline" onClick={downloadDesign}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Télécharger
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="canvas-wrapper" ref={wrapperRef}>
          <div
            className="canvas-container"
            style={{
              width:  canvasW * stageScale,
              height: canvasH * stageScale,
              flexShrink: 0,
            }}
          >
            <div style={{
              width:           canvasW,
              height:          canvasH,
              transform:       `scale(${stageScale})`,
              transformOrigin: 'top left',
              boxShadow:       'var(--shadow-xl)',
              borderRadius:    'var(--radius-sm)',
              overflow:        'hidden',
            }}>
            <Stage
              ref={stageRef}
              width={canvasW}
              height={canvasH}
              onMouseDown={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}
              onTouchStart={(e) => { if (e.target === e.target.getStage()) setSelectedId(null); }}
            >
              <Layer>
                {/* Fond neutre — la couleur est portée par la maquette elle-même */}
                <Rect x={0} y={0} width={canvasW} height={canvasH} fill="#f8f8f8" />

                {/* Image maquette — change selon la couleur sélectionnée */}
                {mockupImageUrl && (
                  <URLImage url={mockupImageUrl} x={0} y={0} width={canvasW} height={canvasH} />
                )}

                {/* Zone d'impression (guide) */}
                <Rect
                  name="printzone"
                  x={printZone.x} y={printZone.y}
                  width={printZone.w} height={printZone.h}
                  fill="transparent"
                  stroke="#3b82f6"
                  strokeWidth={1}
                  dash={[6, 4]}
                  listening={false}
                />

                {/* Éléments */}
                {faceElements.map((el) => (
                  <StudioElement
                    key={el.id}
                    el={el}
                    isSelected={selectedId === el.id}
                    onSelect={setSelectedId}
                    onChange={updateElement}
                    printZone={printZone}
                  />
                ))}
              </Layer>
            </Stage>
            </div>
          </div>

          {/* Légende zone */}
          <p className="print-zone-hint">Zone d'impression délimitée en bleu</p>
        </div>

        {/* ── Panneau propriétés élément sélectionné ── */}
        {selectedEl && (
          <div className="element-props">
            <div className="props-header">
              <h4>{selectedEl.type === 'text' ? '✏️ Modifier le texte' : '🖼 Modifier image'}</h4>
              <button className="prop-btn prop-btn-danger" onClick={deleteSelected}>🗑 Supprimer</button>
            </div>

            {/* ── Texte ── */}
            {selectedEl.type === 'text' && (
              <>
                {/* Ligne 1 : contenu + couleur */}
                <div className="props-row" style={{ marginBottom: 8 }}>
                  <input
                    className="form-input"
                    value={selectedEl.text || ''}
                    onChange={(e) => updateElement({ ...selectedEl, text: e.target.value })}
                    placeholder="Votre texte…"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <input
                    type="color"
                    value={selectedEl.fill || '#000000'}
                    onChange={(e) => updateElement({ ...selectedEl, fill: e.target.value })}
                    className="color-input"
                    title="Couleur du texte"
                  />
                </div>

                {/* Ligne 2 : police + taille + style + alignement */}
                <div className="props-row">
                  <select
                    className="form-input form-select"
                    value={selectedEl.fontFamily || 'Arial'}
                    onChange={(e) => updateElement({ ...selectedEl, fontFamily: e.target.value })}
                    style={{ width: 140 }}
                  >
                    {['Arial','Helvetica','Times New Roman','Courier New','Georgia','Verdana','Impact','Trebuchet MS'].map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>

                  <input
                    type="number" min={8} max={200}
                    value={selectedEl.fontSize || 24}
                    onChange={(e) => updateElement({ ...selectedEl, fontSize: Math.max(8, parseInt(e.target.value) || 24) })}
                    className="form-input"
                    style={{ width: 64 }}
                    title="Taille (px)"
                  />

                  <button
                    className={`prop-btn${selectedEl.bold ? ' active' : ''}`}
                    onClick={() => updateElement({ ...selectedEl, bold: !selectedEl.bold, italic: false })}
                    title="Gras"
                  ><b>B</b></button>

                  <button
                    className={`prop-btn${selectedEl.italic ? ' active' : ''}`}
                    onClick={() => updateElement({ ...selectedEl, italic: !selectedEl.italic, bold: false })}
                    title="Italique"
                  ><i>I</i></button>

                  {/* Alignement */}
                  {['left','center','right'].map(a => (
                    <button
                      key={a}
                      className={`prop-btn${(selectedEl.align || 'left') === a ? ' active' : ''}`}
                      onClick={() => updateElement({ ...selectedEl, align: a })}
                      title={a === 'left' ? 'Gauche' : a === 'center' ? 'Centrer' : 'Droite'}
                    >
                      {a === 'left' ? '⬅' : a === 'center' ? '↔' : '➡'}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ── Image ── */}
            {selectedEl.type === 'image' && (
              <div className="props-row">
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>
                  Glissez les poignées pour redimensionner • Faites glisser pour déplacer
                </span>
                <label className="prop-btn" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  🔄 Remplacer l'image
                  <input type="file" accept="image/*" hidden onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => updateElement({ ...selectedEl, src: ev.target.result });
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
                </label>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Sidebar droite ── */}
      <aside className="studio-sidebar-right">
        {/* Quantités */}
        <div className="sidebar-section">
          <h3 className="sidebar-title">Quantités par taille</h3>
          <div className="qty-grid">
            {Object.keys(quantities).map((size) => (
              <div key={size} className="qty-row">
                <label className="qty-label">{size}</label>
                <div className="qty-controls">
                  <button onClick={() => setQuantities(prev => ({ ...prev, [size]: Math.max(0, (prev[size] || 0) - 1) }))}>−</button>
                  <input
                    type="number" min="0"
                    value={quantities[size] || 0}
                    onChange={(e) => setQuantities(prev => ({ ...prev, [size]: Math.max(0, parseInt(e.target.value) || 0) }))}
                  />
                  <button onClick={() => setQuantities(prev => ({ ...prev, [size]: (prev[size] || 0) + 1 }))}>+</button>
                </div>
              </div>
            ))}
          </div>
          <div className="qty-total">
            Total : <strong>{totalQty} pièces</strong>
          </div>
        </div>

        {/* Estimation */}
        <div className="sidebar-section estimate-section">
          <h3 className="sidebar-title">Estimation de prix</h3>
          {!selectedTech || !totalQty ? (
            <p className="estimate-hint">Choisissez une technique et des quantités pour obtenir une estimation.</p>
          ) : estimate ? (
            <div className="estimate-block">
              <div className="estimate-row">
                <span>Total HT</span>
                <strong>{estimate.total_ht?.toFixed(2)} €</strong>
              </div>
              <div className="estimate-row">
                <span>TVA 20%</span>
                <span>{estimate.tva?.toFixed(2)} €</span>
              </div>
              <div className="estimate-row estimate-total">
                <span>Total TTC</span>
                <strong>{estimate.total_ttc?.toFixed(2)} €</strong>
              </div>
              <div className="estimate-unit">
                Prix unitaire ≈ {estimate.unit_cost?.toFixed(2)} €
              </div>
              <p className="estimate-disclaimer">Estimation indicative, confirmée dans le devis.</p>
            </div>
          ) : <div className="spinner" />}
        </div>

        {/* CTA Devis */}
        <div className="sidebar-section">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => setQuoteModal(true)}
            disabled={!totalQty || !selectedTech}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            Demander un devis
          </Button>
          <p className="cta-hint">Réponse garantie sous 48h</p>
        </div>
      </aside>

      {/* ── Modal ajout texte ── */}
      <Modal isOpen={addTextModal} onClose={() => setAddTextModal(false)} title="Ajouter un texte"
        footer={<div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}><Button variant="ghost" onClick={() => setAddTextModal(false)}>Annuler</Button><Button variant="primary" onClick={addText}>Ajouter</Button></div>}
      >
        <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
          <FormField label="Texte" required>
            <Input
              autoFocus
              value={textConfig.text}
              onChange={(e) => setTextConfig(p => ({ ...p, text: e.target.value }))}
              placeholder="Votre texte ici…"
              onKeyDown={(e) => { if (e.key === 'Enter') addText(); }}
            />
          </FormField>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <FormField label="Taille">
              <Input type="number" min={10} max={200} value={textConfig.fontSize}
                onChange={(e) => setTextConfig(p => ({ ...p, fontSize: parseInt(e.target.value) || 24 }))} />
            </FormField>
            <FormField label="Couleur">
              <input type="color" value={textConfig.fill} style={{width:'100%',height:'42px',cursor:'pointer',border:'1.5px solid var(--border)',borderRadius:'var(--radius-sm)'}}
                onChange={(e) => setTextConfig(p => ({ ...p, fill: e.target.value }))} />
            </FormField>
          </div>
          <FormField label="Police">
            <Select value={textConfig.fontFamily} onChange={(e) => setTextConfig(p => ({ ...p, fontFamily: e.target.value }))}>
              {['Arial','Helvetica','Times New Roman','Courier New','Georgia','Verdana','Impact'].map(f => <option key={f} value={f}>{f}</option>)}
            </Select>
          </FormField>
          <div style={{display:'flex',gap:'8px'}}>
            <button className={`prop-btn${textConfig.bold ? ' active' : ''}`} onClick={() => setTextConfig(p => ({ ...p, bold: !p.bold }))} style={{padding:'8px 14px',borderRadius:'var(--radius-sm)',border:'1.5px solid var(--border)',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}><b>G</b></button>
            <button className={`prop-btn${textConfig.italic ? ' active' : ''}`} onClick={() => setTextConfig(p => ({ ...p, italic: !p.italic }))} style={{padding:'8px 14px',borderRadius:'var(--radius-sm)',border:'1.5px solid var(--border)',fontSize:'14px',cursor:'pointer'}}><i>I</i></button>
          </div>
        </div>
      </Modal>

      {/* ── Modal devis ── */}
      <Modal isOpen={quoteModal} onClose={() => setQuoteModal(false)} title="Demander un devis" size="lg"
        footer={<div style={{display:'flex',gap:'8px',justifyContent:'flex-end'}}><Button variant="ghost" onClick={() => setQuoteModal(false)}>Annuler</Button><Button variant="primary" loading={submitting} onClick={handleSubmitQuote}>Envoyer la demande</Button></div>}
      >
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'16px'}}>
          <FormField label="Prénom">
            <Input value={quoteForm.contact_first_name} onChange={e => setQuoteForm(p => ({ ...p, contact_first_name: e.target.value }))} placeholder="Jean" />
          </FormField>
          <FormField label="Nom">
            <Input value={quoteForm.contact_last_name} onChange={e => setQuoteForm(p => ({ ...p, contact_last_name: e.target.value }))} placeholder="Dupont" />
          </FormField>
          <FormField label="Email" required>
            <Input type="email" value={quoteForm.contact_email} onChange={(e) => setQuoteForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="email@entreprise.fr" />
          </FormField>
          <FormField label="Téléphone">
            <Input type="tel" value={quoteForm.contact_phone} onChange={(e) => setQuoteForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="+33 6 12 34 56 78" />
          </FormField>
          <FormField label="Entreprise">
            <Input value={quoteForm.contact_company} onChange={(e) => setQuoteForm(p => ({ ...p, contact_company: e.target.value }))} placeholder="Mon Entreprise SAS" />
          </FormField>
          <div style={{gridColumn:'1/-1'}}>
            <FormField label="Informations complémentaires">
              <Textarea value={quoteForm.notes} onChange={(e) => setQuoteForm(p => ({ ...p, notes: e.target.value }))} rows={3} placeholder="Précisions sur votre commande, couleurs Pantone, délais souhaités…" />
            </FormField>
          </div>
        </div>
        {estimate && (
          <div className="quote-estimate-recap">
            <p>Estimation : <strong>{estimate.total_ht?.toFixed(2)} € HT</strong> ({totalQty} pièces)</p>
          </div>
        )}
      </Modal>


    </div>
  );
};

export default StudioPage;